/**
 * PromptsIaTab — vista del Designer que muestra el preview del prompt
 * que el agente IA recibirá al generar una sección concreta de la
 * plantilla (Doc 24 §3 Fase 12).
 *
 * Composición:
 *   - Selector de sección (dropdown plano, jerarquía aplanada).
 *   - Selector de locale (es/en — solo locales activos del doc).
 *   - Render del markdown construido por `buildPromptPreview` (Doc 24)
 *     pasado por `marked` con clase `prose-hiva` — mismo patrón que el
 *     diff-block de comentarios, así obtenemos tablas GFM, **bold**,
 *     *italic*, listas anidadas y code blocks sin reinventar nada.
 *   - Botón "Copiar prompt" → clipboard del markdown crudo.
 *
 * Seguridad del HTML: el markdown viene de `buildPromptPreview` (builder
 * controlado por nosotros) — no es contenido user-generated. Mismo
 * pattern del `CommentDiffBlock`, sin sanitización extra.
 */

import { Check, ClipboardCopy } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { marked } from 'marked'

import {
  buildPromptPreview,
  flattenSections,
} from '../../../lib/templatePromptPreview'

import type { DesignerState } from './state'
import type { SupportedLocale } from '../../../i18n/config'

interface PromptsIaTabProps {
  state: DesignerState
}

export function PromptsIaTab({ state }: PromptsIaTabProps) {
  const { t } = useTranslation('templates')
  const definition = state.definition

  // Lista plana de secciones (incluyendo descendientes).
  const flat = useMemo(
    () => flattenSections(definition.sections),
    [definition.sections],
  )

  const activeLocales = useMemo(
    () => Object.keys(definition.i18n) as SupportedLocale[],
    [definition.i18n],
  )

  // Selección inicial: primera sección del árbol + locale activo del designer.
  const initialSectionId = flat[0]?.section_id ?? ''
  const [selectedSectionId, setSelectedSectionId] = useState<string>(
    initialSectionId,
  )
  const [selectedLocale, setSelectedLocale] = useState<SupportedLocale>(
    activeLocales.includes(state.activeLocale)
      ? state.activeLocale
      : (activeLocales[0] ?? 'es'),
  )
  const [copied, setCopied] = useState(false)

  // Re-derivar selección si la sección actual ya no existe (p.ej. fue
  // borrada en otra tab). useMemo asegura idempotencia.
  const effectiveSectionId = useMemo(() => {
    if (flat.some((s) => s.section_id === selectedSectionId)) {
      return selectedSectionId
    }
    return flat[0]?.section_id ?? ''
  }, [flat, selectedSectionId])

  const markdown = useMemo(() => {
    if (!effectiveSectionId) return ''
    return buildPromptPreview({
      definition,
      sectionId: effectiveSectionId,
      locale: selectedLocale,
    })
  }, [definition, effectiveSectionId, selectedLocale])

  // Markdown → HTML con `marked` (GFM tablas y formato inline activos
  // por default desde v15). Try/catch defensivo: si por alguna razón
  // el parse falla, caemos a render plano para no romper la vista.
  const html = useMemo(() => {
    if (!markdown) return ''
    try {
      return marked.parse(markdown, { async: false }) as string
    } catch {
      return ''
    }
  }, [markdown])

  async function copyToClipboard(): Promise<void> {
    if (typeof navigator === 'undefined' || !navigator.clipboard) return
    try {
      await navigator.clipboard.writeText(markdown)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1500)
    } catch (err) {
      console.error('Clipboard write failed', err)
    }
  }

  // Empty state si la plantilla no tiene secciones.
  if (flat.length === 0) {
    return (
      <div
        className="flex-1 min-h-0 overflow-y-auto bg-canvas"
        data-testid="prompts-ia-tab"
      >
        <div className="max-w-3xl mx-auto py-12 px-4 text-center">
          <p className="text-sm text-ink-muted italic">
            {t('designer.prompts_ia.no_sections_yet')}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div
      className="flex-1 min-h-0 overflow-y-auto bg-canvas"
      data-testid="prompts-ia-tab"
    >
      <div className="max-w-4xl mx-auto py-6 px-4 space-y-4">
        {/* Header descriptivo */}
        <header>
          <h2 className="font-display text-xl font-semibold text-ink">
            {t('designer.prompts_ia.title')}
          </h2>
          <p className="mt-1 text-xs text-ink-subtle">
            {t('designer.prompts_ia.subtitle')}
          </p>
        </header>

        {/* Controles: sección + locale + copy */}
        <div className="flex items-end gap-3 flex-wrap">
          <label className="block min-w-0 flex-1">
            <span className="text-[10px] uppercase tracking-wide font-medium text-ink-subtle">
              {t('designer.prompts_ia.section_label')}
            </span>
            <select
              value={effectiveSectionId}
              onChange={(e) => setSelectedSectionId(e.target.value)}
              data-testid="prompts-ia-section-select"
              className="mt-1 w-full bg-canvas border border-line rounded-md px-2 py-1.5 text-sm focus:outline-none focus:border-brand"
            >
              {flat.map((s) => {
                const i18n = definition.i18n[selectedLocale]?.sections.find(
                  (x) => x.section_id === s.section_id,
                )
                const title = i18n?.title ?? s.section_id
                return (
                  <option key={s.section_id} value={s.section_id}>
                    {title} — {s.section_id}
                  </option>
                )
              })}
            </select>
          </label>

          <div>
            <span className="block text-[10px] uppercase tracking-wide font-medium text-ink-subtle mb-1">
              {t('designer.prompts_ia.locale_label')}
            </span>
            <div className="flex items-center gap-1">
              {activeLocales.map((loc) => (
                <button
                  key={loc}
                  type="button"
                  onClick={() => setSelectedLocale(loc)}
                  data-testid={`prompts-ia-locale-${loc}`}
                  className={
                    loc === selectedLocale
                      ? 'px-2.5 py-1 text-xs font-mono uppercase rounded bg-brand-soft text-brand'
                      : 'px-2.5 py-1 text-xs font-mono uppercase rounded border border-line text-ink-muted hover:text-ink'
                  }
                >
                  {loc}
                </button>
              ))}
            </div>
          </div>

          <button
            type="button"
            onClick={copyToClipboard}
            data-testid="prompts-ia-copy"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border border-line text-ink hover:bg-surface-muted"
          >
            {copied ? (
              <Check className="w-3.5 h-3.5 text-official" />
            ) : (
              <ClipboardCopy className="w-3.5 h-3.5" />
            )}
            {copied
              ? t('designer.prompts_ia.copied')
              : t('designer.prompts_ia.copy')}
          </button>
        </div>

        {/* Render del prompt. `prose-hiva` aplica estilos de
            tipografía, listas, tablas GFM (`| col | col |`), bold,
            italic, code, blockquote, etc. — mismo set de reglas que
            usan el CommentDiffBlock y los pre-views del artifact. */}
        <article
          className="rounded-md border border-line bg-surface px-5 py-4 text-sm text-ink prose-hiva"
          data-testid="prompts-ia-preview"
          dangerouslySetInnerHTML={{ __html: html }}
        />
      </div>
    </div>
  )
}
